import { Department } from '../department.model';
import { UserRole } from '../user-role.model';
import { User } from '../user/user.model';
import { EvaluationCycle } from '../evaluation/evaluation-cycle.model';
import { Site } from '../site.model';
import { Role } from '@app/domain/role/model/role.model';

export interface BulkUpdateResponseDto {
  /**
   * A list of newly created users.
   * If validation is fails then will be null
   */
  success: null | Array<User>
  /**
   * A list of UserDtos If validation succeeds then will be null
   */
  failure: null | Array<BulkUploadValidationDto>
}

export interface BulkUploadValidationDto {
  value: BulkUpdateUserDto;
  valid: boolean;
  errors: Array<BulkUploadValidationErrorDto>;
}

export interface BulkUploadValidationErrorDto {
  value: string;
  errorType: BulkUploadValidationErrorType;
  message: string;
}

export class BulkUpdateUserDto {
  id?: number;
  creationOrder?: number | null;
  state?: BulkUploadUserState | null;

  constructor(
    public firstName: string,
    public lastName: string,
    public email: string,
    public startDate: string,
    public department: string | null,
    public site: string | null,
    public role: string,
    public managerEmail: string,
    public position: string,
    public grade: string,
  ) { }
}

export class BulkUpdateRow {
  constructor(
    public firstName: BulkUpdateColumn,
    public lastName: BulkUpdateColumn,
    public email: BulkUpdateColumn,
    public startDate: BulkUpdateColumn,
    public department: BulkUpdateColumn,
    public site: BulkUpdateColumn,
    public role: BulkUpdateColumn,
    public managerEmail: BulkUpdateColumn,
    public position: BulkUpdateColumn,
    public grade: BulkUpdateColumn,
  ) { }

  toBulkUpdateUserDto(): BulkUpdateUserDto {
    const department = ((this.department.value && this.department.value.length > 0) ? this.department.value : null);
    const site = ((this.site.value && this.site.value.length > 0) ? this.site.value : null);
    return new BulkUpdateUserDto(
      this.firstName.value,
      this.lastName.value,
      this.email.value,
      this.startDate.value,
      department,
      site,
      this.role.value,
      this.managerEmail.value,
      this.position.value,
      this.grade.value
    );
  }
}

export class BulkUpdateColumn {
  public originalValue: string;

  constructor(
    public value: string,
    public type: BulkUpdateColumnType,
    public error: null | BulkUpdateValidationError = null,
    public warning: null | BulkUpdateValidationError = null,
  ) {
    this.originalValue = value;
  }
}

export enum BulkUpdateColumnType {
  FIRST_NAME = 'FIRST_NAME',
  LAST_NAME = 'LAST_NAME',
  EMAIL = 'EMAIL',
  START_DATE = 'START_DATE',
  DEPARTMENT = 'DEPARTMENT',
  SITE = 'SITE',
  ROLE = 'ROLE',
  MANAGER_EMAIL = 'MANAGER_EMAIL',
  POSITION = 'POSITION',
  GRADE = 'GRADE'
}

export enum BulkUploadValidationErrorType {
  REQUIRED = 'REQUIRED',
  MIN_LENGTH = 'MIN_LENGTH',
  MAX_LENGTH = 'MAX_LENGTH',
  EMAIL_INVALID_FORMAT = 'EMAIL_INVALID_FORMAT',
  EMAIL_INVALID_NOT_FOUND = 'EMAIL_INVALID_NOT_FOUND',
  EMAIL_INVALID_DUPLICATE_FORM = 'EMAIL_INVALID_DUPLICATE_FORM',
  ROLE_INVALID_NOT_FOUND = 'ROLE_INVALID_NOT_FOUND',
  ROLE_INVALID_MANAGER_REQUIRED = 'ROLE_INVALID_MANAGER_REQUIRED',
  DEPARTMENT_INVALID_NOT_FOUND = 'DEPARTMENT_INVALID_NOT_FOUND',
  SITE_INVALID_NOT_FOUND = 'SITE_INVALID_NOT_FOUND',
  START_DATE_INVALID_FORMAT = 'START_DATE_INVALID_FORMAT',
  POSITION_INVALID_NOT_FOUND = 'POSITION_INVALID_NOT_FOUND ',
  GRADE_INVALID_NOT_FOUND = 'GRADE_INVALID_NOT_FOUND',
  MANAGER_EMAIL_INVALID_CIRCULAR_DEPENDENCY = 'MANAGER_EMAIL_INVALID_CIRCULAR_DEPENDENCY',
  EMAIL_INVALID_DUPLICATE_COMPANY = 'EMAIL_INVALID_DUPLICATE_COMPANY',
  EMAIL_INVALID_DUPLICATE_OTHER_COMPANY = 'EMAIL_INVALID_DUPLICATE_OTHER_COMPANY',
  MANAGER_EMAIL_INVALID_CHANGED_ACTIVE_EVALUATION = 'MANAGER_EMAIL_INVALID_CHANGED_ACTIVE_EVALUATION'
}

export class BulkUpdateValidationError {
  constructor(
    public message: string,
    public type: BulkUploadValidationErrorType
  ) { }
}

export class PositionDenormalized {
  constructor(
    public name: string,
    public grades: Array<string>
  ) { }
}

/**
 * @param {Array} rows - TODO: rows
 * @param {Array} users - TODO: users
 * @param {Array} roles - TODO: roles
 * @param {Array} departments - TODO: departments
 * @param {Array} sites - TODO: sites
 * @param {Array} positions - TODO: positions
 * @param {Array} positionsDenormalized - TODO: positionsDenormalized
 * @param {boolean} showWarnings - If true, warning validation will be run
 */
export class BulkUploadValidationData {
  constructor(
    public type: BulkUploadType,
    public rows: Array<BulkUpdateRow>,
    public users: Array<User>,
    public roles: Array<UserRole>,
    public departments: Array<Department>,
    public sites: Array<Site>,
    public positions: Array<Role>,
    public positionsDenormalized: Array<PositionDenormalized>,
    public showWarnings: boolean,
    public existingUsers: Array<string>,
    public cycles: Array<EvaluationCycle>
  ) { }
}

export interface BulkUpdateErrorDto {
  value: string,
  type: BulkUploadValidationErrorType
  message: string
}


export interface BulkUpload {
  id: number;
  companyId: number;
  creationDate: Date | null;
  completionDate: Date | null;
  type: BulkUploadType;
  state: BulkUploadState;
  data: Array<BulkUpdateUserDto>;
}

export enum BulkUploadType {
  CREATE = 'CREATE',
  UPDATE = 'UPDATE',
  ARCHIVE = 'ARCHIVE'
  /**
   *     USER_CREATE,
    USER_UPDATE,
    USER_ARCHIVE
   */
}

export enum BulkUploadState {
  STARTED = 'STARTED',
  FAILED = 'FAILED',
  COMPLETE = 'COMPLETE'
}

export enum BulkUploadUserState {
  PENDING = 'PENDING',
  FAILED = 'FAILED',
  COMPLETE = 'COMPLETE'
}
